import { useEffect, useState, useCallback } from "react";
import api, { API, formatErr } from "../../lib/api";
import { toast } from "sonner";
import MediaUploader from "../MediaUploader";
import { Send, Eye, Image as ImageIcon, X, Users, History, Sparkles } from "lucide-react";

export default function BroadcastsAdmin() {
  const [form, setForm] = useState({ subject: "", body_html: "", images: [], audience: "customers_only" });
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [customerCount, setCustomerCount] = useState(0);

  const loadHistory = useCallback(async () => {
    try {
      const [h, c] = await Promise.all([
        api.get("/admin/broadcasts"),
        api.get("/admin/customers?limit=1000"),
      ]);
      setHistory(h.data);
      setCustomerCount(c.data.length);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const onMediaChange = ({ images }) => setForm(f => ({ ...f, images }));

  const preview = async () => {
    if (!form.subject || !form.body_html) { toast.error("Add a subject and body first"); return; }
    setPreviewing(true);
  };

  const send = async () => {
    if (!form.subject || !form.body_html) { toast.error("Subject + body required"); return; }
    if (!confirm(`Send this email to ${customerCount} customers? This cannot be undone.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcasts", form);
      toast.success(`Sent ${data.sent} of ${data.recipient_count} emails${data.failure_count ? ` · ${data.failure_count} failed` : ""}`, { duration: 8000 });
      setForm({ subject: "", body_html: "", images: [], audience: "customers_only" });
      loadHistory();
    } catch (e) { toast.error(formatErr(e), { duration: 8000 }); } finally { setSending(false); }
  };

  return (
    <div className="space-y-10" data-testid="broadcasts-admin">
      <div className="flex items-end justify-between flex-wrap gap-4 pb-5 border-b border-[rgba(244,244,240,0.05)]">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Atelier · Newsletter</p>
          <h2 className="font-serif text-4xl">Broadcast Email</h2>
          <p className="text-xs text-muted mt-2 max-w-xl">Send an announcement, offer, or lookbook drop to every registered customer. Uses your verified sender + email logo automatically.</p>
        </div>
        <div className="bg-[#141414] px-5 py-3 border border-[rgba(244,244,240,0.06)] flex items-center gap-3">
          <Users size={14} strokeWidth={1.5} className="text-gold" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-muted">Audience</p>
            <p className="font-serif text-xl leading-none mt-1" data-testid="audience-count">{customerCount} customers</p>
          </div>
        </div>
      </div>

      {/* Composer */}
      <section className="bg-[#141414] p-6 border border-[rgba(244,244,240,0.05)] space-y-5">
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-1.5">Subject</label>
          <input
            type="text" value={form.subject}
            onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
            placeholder="The Winter Edit is here · 20% off ends Sunday"
            className="input-line w-full"
            maxLength={200}
            data-testid="broadcast-subject"
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-1.5">Message (HTML supported)</label>
          <textarea
            rows={8}
            value={form.body_html}
            onChange={e => setForm(f => ({ ...f, body_html: e.target.value }))}
            placeholder={"<p>Dear {{customer_first_name}},</p>\n<p>Our newest capsule launches today. Sculptural silks, hand-finished in Bengaluru.</p>\n<p><a href='https://goshifashion.com/shop' style='color:#C8A97E;'>Explore the edit →</a></p>"}
            className="input-line w-full !min-h-[220px] !py-3 font-mono text-sm resize-y"
            data-testid="broadcast-body"
          />
          <div className="mt-2 bg-[#0C0C0C] p-3 border border-[rgba(244,244,240,0.05)]">
            <p className="text-[10px] uppercase tracking-[0.22em] text-gold mb-1.5">Personalisation & formatting</p>
            <div className="flex flex-wrap gap-2 text-[10px] text-muted">
              <code className="bg-[#141414] px-2 py-1" data-testid="var-full-name">{"{{customer_full_name}}"}</code>
              <code className="bg-[#141414] px-2 py-1" data-testid="var-first-name">{"{{customer_first_name}}"}</code>
              <code className="bg-[#141414] px-2 py-1" data-testid="var-email">{"{{email}}"}</code>
            </div>
            <p className="text-[10px] text-muted/80 mt-2 leading-relaxed">
              Tokens above are replaced per recipient before sending. Hyperlinks are fully supported — use
              <code className="bg-[#141414] px-1.5 py-0.5 mx-1">{'<a href="https://…">'}</code>
              for calls-to-action. Basic HTML: {"<p> <strong> <em> <ul> <li> <img>"} are all allowed.
            </p>
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-2 flex items-center gap-1.5"><ImageIcon size={11} strokeWidth={1.5} /> Attachments (up to 4 images)</label>
          <MediaUploader images={form.images} videos={[]} onChange={onMediaChange} accept="image" />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-1.5">Audience</label>
          <select
            value={form.audience}
            onChange={e => setForm(f => ({ ...f, audience: e.target.value }))}
            className="input-line !bg-transparent"
            data-testid="broadcast-audience"
          >
            <option value="customers_only" className="bg-[#0C0C0C]">Customers only (excludes admin accounts)</option>
            <option value="all" className="bg-[#0C0C0C]">Everyone registered</option>
          </select>
        </div>

        <div className="flex gap-3 pt-2 border-t border-[rgba(244,244,240,0.05)]">
          <button onClick={preview} className="btn-outline" data-testid="broadcast-preview-btn">
            <Eye size={12} strokeWidth={1.5} className="inline mr-1.5 -mt-0.5" /> Preview
          </button>
          <button onClick={send} disabled={sending} className="btn-gold flex-1" data-testid="broadcast-send-btn">
            <Send size={12} strokeWidth={1.5} className="inline mr-1.5 -mt-0.5" />
            {sending ? "Sending…" : `Send to ${customerCount} customers`}
          </button>
        </div>
      </section>

      {/* Recent broadcasts */}
      <section>
        <h3 className="text-xs uppercase tracking-[0.25em] text-gold mb-4 flex items-center gap-2"><History size={12} strokeWidth={1.5} /> Recent broadcasts</h3>
        {history.length === 0 ? (
          <p className="text-muted text-sm bg-[#141414] p-6 border border-[rgba(244,244,240,0.05)]">
            No broadcasts sent yet. Craft your first one above <Sparkles size={11} className="inline text-gold" />
          </p>
        ) : (
          <ul className="space-y-3">
            {history.map(b => (
              <li key={b.broadcast_id} className="bg-[#141414] p-5 border border-[rgba(244,244,240,0.05)]" data-testid={`broadcast-history-${b.broadcast_id}`}>
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div className="min-w-0">
                    <p className="font-serif text-lg truncate">{b.subject}</p>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted mt-1">
                      {new Date(b.at).toLocaleString()} · {b.audience.replace("_", " ")}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-gold font-serif text-xl">{b.sent}<span className="text-muted text-sm"> / {b.recipient_count}</span></p>
                    <p className="text-[9px] uppercase tracking-[0.18em] text-muted">delivered</p>
                    {b.failure_count > 0 && (
                      <p className="text-[10px] uppercase tracking-[0.18em] text-red-400 mt-1">{b.failure_count} failed</p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Preview drawer */}
      {previewing && (
        <div className="fixed inset-0 z-[90] flex items-stretch" data-testid="broadcast-preview-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setPreviewing(false)} />
          <div className="relative ml-auto bg-[#0C0C0C] border-l border-[rgba(244,244,240,0.08)] w-full max-w-3xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-[rgba(244,244,240,0.06)]">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Preview</p>
                <h3 className="font-serif text-xl truncate max-w-md">{form.subject}</h3>
              </div>
              <button onClick={() => setPreviewing(false)} className="text-muted hover:text-gold"><X size={20} strokeWidth={1.5} /></button>
            </div>
            <PreviewIframe form={form} />
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewIframe({ form }) {
  const [html, setHtml] = useState("");
  useEffect(() => {
    api.post("/admin/broadcasts/preview", form).then(({ data }) => setHtml(data)).catch(() => setHtml(""));
  }, [form]);
  return <iframe title="Broadcast preview" srcDoc={html} className="flex-1 w-full bg-[#0C0C0C]" data-testid="broadcast-preview-iframe" />;
}
